<template>
  <div
    class="home"
    ref="pchome"
    :style="{
      '--hero-bg-max-height': heroBgMaxHeight,
    }"
  >
    <!-- 批量上传弹窗 -->
    <van-popup
      v-model="showImportDialog"
      round
      position="center"
      closeable
      style="width: 420px; max-width: 90vw"
      class="import-popup"
    >
      <div class="import-dialog">
        <!-- 批量上传供需信息 -->
        <h3 class="import-title">{{ $t("pcHome.bulkUploadTitle") }}</h3>
        <!-- 请上传 Excel 文件，支持 .xlsx、.xls 格式 -->
        <p class="import-desc">{{ $t("pcHome.bulkUploadDescription") }}</p>
        <van-uploader
          :after-read="handleFileUpload"
          :before-delete="beforeDeleteFile"
          v-model="importFileList"
          multiple
          :max-count="1"
          accept=".xlsx,.xls"
          :preview-size="80"
        >
          <div class="upload-trigger">
            <svg
              width="40"
              height="40"
              viewBox="0 0 48 48"
              fill="none"
              stroke="#999"
              stroke-width="1.5"
            >
              <path
                d="M28 16H12v14h4V22h12v8h4V16zM12 8v4h24V8h-4l-4 4H16l-4-4H12z"
              />
              <path
                d="M40 44H8c-2.2 0-4-1.8-4-4V8c0-2.2 1.8-4 4-4h32c2.2 0 4 1.8 4 4v32c0 2.2-1.8 4-4 4zM8 8v32h32V8H8z"
              />
            </svg>
            <!-- 点击上传文件 -->
            <span class="upload-text">{{ $t("pcHome.chooseFile") }}</span>
          </div>
        </van-uploader>
        <div class="import-actions">
          <!-- 取消 -->
          <van-button plain size="small" @click="handleCancel">{{
            $t("pcHome.cancel")
          }}</van-button>
          <!-- 确定上传 -->
          <van-button type="primary" size="small" @click="submitImport">{{
            $t("pcHome.confirmUpload")
          }}</van-button>
        </div>
      </div>
    </van-popup>
    <!-- 背景图 -->
    <div class="hero-banner">
      <img :src="logoList[1].image" alt="Hero Banner" @load="updateLayout" />
      <div class="hero-search-area" role="search">
        <div class="hero-search-main">
          <img class="hero-search-logo" :src="logoList[0].image" alt="XSOU" />
          <div class="hero-search-shell">
            <!-- 搜索料号、名称 -->
            <input
              v-model.trim="heroSearchKeyword"
              class="hero-search-input"
              type="search"
              :placeholder="$t('pcHome.searchPlaceholder')"
              :aria-label="$t('pcHome.searchPlaceholder')"
              @keyup.enter="onProductSearch(heroSearchKeyword)"
            />
            <!-- 先搜一下 -->
            <button
              class="hero-search-button"
              type="button"
              @click="onProductSearch(heroSearchKeyword)"
            >
              {{ $t("pcHome.searchAction") }}
            </button>
          </div>
          <!-- 先搜服务价值 -->
          <div
            class="hero-value-strip"
            :aria-label="$t('pcHome.serviceValues')"
          >
            <div class="hero-value-item">
              <img :src="require('@/img/home/1.png')" alt="" />
              <!-- 卓越服务 -->
              <span>{{ $t("pcHome.excellentService") }}</span>
            </div>
            <div class="hero-value-item">
              <img :src="require('@/img/home/2.png')" alt="" />
              <!-- 持续创造 -->
              <span>{{ $t("pcHome.continuousInnovation") }}</span>
            </div>
            <div class="hero-value-item">
              <img :src="require('@/img/home/3.png')" alt="" />
              <!-- 协作共赢 -->
              <span>{{ $t("pcHome.winWinCooperation") }}</span>
            </div>
            <div class="hero-value-item">
              <img :src="require('@/img/home/4.png')" alt="" />
              <!-- 价值共享 -->
              <span>{{ $t("pcHome.sharedValue") }}</span>
            </div>
          </div>
        </div>
        <!-- 先搜AI -->
        <button class="hero-ai-button" type="button" @click="openAiAssistant">
          {{ $t("pcHome.ai") }}
        </button>
      </div>
    </div>

    <div class="main-container" id="pc-home-main">
      <!-- 品牌展示 -->
      <section class="brand-showcase" aria-labelledby="home-brand-title">
        <div class="brand-showcase-header">
          <div class="brand-showcase-heading">
            <!-- 热门品牌 -->
            <h2 id="home-brand-title">{{ $t("pcHome.popularBrands") }}</h2>
          </div>
          <!-- 查看更多品牌 -->
          <button
            class="brand-showcase-more"
            type="button"
            @click="goBrandHall"
          >
            {{ $t("pcHome.moreBrands") }}
            <i class="el-icon-arrow-right"></i>
          </button>
        </div>

        <!-- 品牌加载中... -->
        <div v-if="brandShowcaseLoading" class="brand-showcase-state">
          {{ $t("pcHome.brandsLoading") }}
        </div>
        <div v-else-if="brandShowcaseItems.length" class="brand-showcase-grid">
          <button
            v-for="(item, index) in brandShowcaseItems"
            :key="item.id || item.value || `${getBrandName(item)}-${index}`"
            class="brand-showcase-item"
            type="button"
            @click="openBrand(item)"
          >
            <span class="brand-showcase-card">
              <span class="brand-showcase-logo">
                <img
                  v-if="item.logo"
                  :src="item.logo"
                  :alt="getBrandName(item)"
                />
                <span v-else>{{ getBrandInitial(item) }}</span>
              </span>
            </span>
          </button>
        </div>
        <!-- 暂无品牌数据 -->
        <div v-else class="brand-showcase-state">
          {{ $t("pcHome.noBrands") }}
        </div>
      </section>

      <!-- 快捷入口 -->
      <div v-if="navList && navList.length > 0" class="shortcut-wrapper">
        <div class="shortcut-container">
          <!-- 向左滑动快捷入口 -->
          <button
            v-if="shortcutScrollable"
            class="shortcut-scroll-button shortcut-scroll-button-left"
            type="button"
            :aria-label="$t('pcHome.scrollShortcutsLeft')"
            @click="scrollShortcuts(-1)"
          >
            <i class="el-icon-arrow-left"></i>
          </button>
          <div
            ref="shortcutViewport"
            class="shortcut-viewport"
            :class="{
              'shortcut-fade-left': canScrollShortcutsLeft,
              'shortcut-fade-right': canScrollShortcutsRight,
            }"
            @scroll.passive="updateShortcutScrollState"
          >
            <div class="shortcut-track">
              <div
                v-for="entry in shortcutCarouselItems"
                :key="entry.key"
                class="shortcut-item"
                @click="onJump(entry.item)"
              >
                <div class="shortcut-icon">
                  <!-- 图标 -->
                  <img :src="entry.item.imgUrl" :alt="$t('pcHome.shortcutIcon')" />
                </div>
                <div class="shortcut-text">{{ localizedShortcutLabel(entry.item) }}</div>
              </div>
            </div>
          </div>
          <!-- 向右滑动快捷入口 -->
          <button
            v-if="shortcutScrollable"
            class="shortcut-scroll-button shortcut-scroll-button-right"
            type="button"
            :aria-label="$t('pcHome.scrollShortcutsRight')"
            @click="scrollShortcuts(1)"
          >
            <i class="el-icon-arrow-right"></i>
          </button>
        </div>
      </div>

      <!-- 商品与信息 -->
      <div class="goods-section">
        <div class="section-header" v-if="tabs.length > 0">
          <div class="tab-nav">
            <div
              v-for="(tab, index) in tabs"
              :key="index"
              :class="['tab-nav-item', { active: activeTab === index }]"
              @click="activeTab = index"
            >
              {{ tab.title }}
            </div>
          </div>
          <div class="section-actions">
            <!-- 导入供需 -->
            <el-button
              v-if="activeTab === 1"
              type="warning"
              size="small"
              @click="showImportDialog = true"
            >
              {{ $t("pcHome.importSupplyDemand") }}
            </el-button>
            <div v-if="activeTab === 0" class="view-toggle">
              <button
                :class="['view-btn', { active: viewMode === 'grid' }]"
                @click="viewMode = 'grid'"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                </svg>
                <!-- 图片 -->
                <span>{{ $t("pcHome.gridView") }}</span>
              </button>
              <button
                :class="['view-btn', { active: viewMode === 'table' }]"
                @click="viewMode = 'table'"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                  <line x1="10" y1="6" x2="10" y2="18" />
                  <line x1="14" y1="6" x2="14" y2="18" />
                </svg>
                <!-- 列表 -->
                <span>{{ $t("pcHome.listView") }}</span>
              </button>
            </div>
          </div>
        </div>
        <div class="section-content">
          <!-- 商品标签页 -->
          <div v-if="activeTab === 0">
            <!-- 我们是有底线的 -->
            <!-- 获取数据失败，请重新加载 -->
            <van-list
              ref="goodsListContainer"
              v-model="loading"
              :finished="finished"
              :finished-text="$t('pcHome.endOfList')"
              :error-text="$t('pcHome.loadFailedRetry')"
              @load="onLoad"
            >
              <!-- 图片视图 -->
              <div v-if="viewMode === 'grid'" class="goods-grid">
                <GoodsItem
                  v-for="(item, index) in list"
                  :key="index"
                  :item="item"
                  :show-part-number="true"
                  @click="onJump(item)"
                ></GoodsItem>
              </div>

              <!-- 表格视图 -->
              <div class="table-view-container" v-else>
                <el-table
                  class="goods-table"
                  :data="list"
                  :border="false"
                  :show-overflow-tooltip="true"
                  @row-click="onJump"
                >
                  <!-- 图片 -->
                  <el-table-column
                    prop="goodsImage"
                    class-name="goods-image-cell"
                    :label="$t('pcHome.image')"
                    width="80"
                    align="center"
                  >
                    <template #default="{ row }">
                      <img
                        :src="row.goodsImage"
                        :alt="$t('pcHome.productImage')"
                        style="
                          width: 50px;
                          height: 50px;
                          object-fit: cover;
                          border-radius: 6px;
                        "
                      />
                    </template>
                  </el-table-column>
                  <!-- <el-table-column prop="goodsName" :label="$t('reportDashboard.goodsName')" min-width="200" /> -->
                  <!-- 品牌 -->
                  <el-table-column
                    prop="goodsBrand"
                    :label="$t('pcHome.brand')"
                    min-width="80"
                    align="center"
                  />
                  <!-- 料号 -->
                  <el-table-column
                    prop="goodsNum"
                    :label="$t('pcHome.partNumber')"
                    min-width="140"
                    align="center"
                  />
                  <!-- 类型 -->
                  <el-table-column
                    prop="productType"
                    :label="$t('pcHome.type')"
                    min-width="80"
                    align="center"
                  />
                  <!-- 容量 -->
                  <el-table-column
                    prop="capacity"
                    :label="$t('pcHome.capacity')"
                    min-width="80"
                    align="center"
                  />
                  <!-- 架构 -->
                  <el-table-column
                    prop="architecture"
                    :label="$t('pcHome.architecture')"
                    min-width="80"
                    align="center"
                  />
                  <!-- 速率 -->
                  <el-table-column
                    prop="speed"
                    :label="$t('pcHome.speed')"
                    min-width="80"
                    align="center"
                  />
                  <!-- 球位 -->
                  <el-table-column
                    prop="ball"
                    :label="$t('pcHome.ballCount')"
                    min-width="80"
                    align="center"
                  />
                  <el-table-column
                    prop="manufactureDate"
                    label="DC"
                    min-width="80"
                    align="center"
                  />
                  <!-- 价格 -->
                  <el-table-column
                    prop="salePrice"
                    :label="$t('pcHome.price')"
                    min-width="120"
                    align="center"
                  >
                    <template #default="{ row }">
                      <span style="font-weight: 600; color: #4286f5">
                        {{
                          row.priceUnit === 0
                            ? "CNY"
                            : row.priceUnit === 10
                              ? "USD"
                              : $t("auth.sentenceSeparator")
                        }}
                        {{ $formatPrice(row.salePrice) }}
                      </span>
                    </template>
                  </el-table-column>
                  <!-- 数量 -->
                  <el-table-column
                    prop="goodsQuantity"
                    :label="$t('pcHome.quantity')"
                    min-width="80"
                    align="center"
                  >
                    <template #default="{ row }">
                      {{ row.quantity }} {{ row.goodsUnit || $t("auth.sentenceSeparator") }}
                    </template>
                  </el-table-column>
                  <!-- 发布人 -->
                  <el-table-column
                    prop="shopContacts"
                    :label="$t('pcHome.publisher')"
                    width="80"
                    align="center"
                  />
                </el-table>
              </div>

              <!-- 暂无数据 -->
              <el-empty
                v-if="list && list.length <= 0"
                :description="$t('pcHome.noData')"
              />
            </van-list>
          </div>
          <!-- 信息标签页 -->
          <div v-if="activeTab === 1">
            <!-- 筛选条件 -->
            <div class="info-filter-container" ref="infoFilterContainer">
              <div class="filter-header">
                <!-- 筛选条件 -->
                <span class="filter-title">{{ $t("pcHome.filters") }}</span>
              </div>
              <div class="filter-body">
                <div class="filter-row">
                  <!-- 品牌： -->
                  <span class="filter-label">{{
                    $t("pcHome.brandLabel")
                  }}</span>
                  <div class="filter-options brand-filter-options">
                    <div class="brand-options-scroll">
                      <!-- 全部 -->
                      <span
                        class="filter-chip"
                        :class="{ active: infoFilters.brand === '' }"
                        @click="selectBrand('')"
                        >{{ $t("pcHome.all") }}</span
                      >
                      <span
                        v-for="brand in visibleBrands"
                        :key="brand.value"
                        class="filter-chip"
                        :class="{ active: infoFilters.brand === brand.text }"
                        @click="selectBrand(brand.text)"
                        >{{ brand.text }}</span
                      >
                      <!-- 搜索 -->
                      <span
                        class="filter-more"
                        @click="openBrandSearchDialog"
                        >{{ $t("pcHome.search") }}</span
                      >
                    </div>
                  </div>
                </div>
                <div class="filter-row">
                  <!-- 货源地： -->
                  <span class="filter-label">{{
                    $t("pcHome.originLabel")
                  }}</span>
                  <div class="filter-options">
                    <!-- 全部 -->
                    <span
                      class="filter-chip"
                      :class="{ active: infoFilters.type === '' }"
                      @click="selectType('')"
                      >{{ $t("pcHome.all") }}</span
                    >
                    <!-- 大陆 -->
                    <span
                      class="filter-chip"
                      :class="{ active: infoFilters.type === '1' }"
                      @click="selectType('1')"
                      >{{ $t("pcHome.mainland") }}</span
                    >
                    <!-- 港澳台 -->
                    <span
                      class="filter-chip"
                      :class="{ active: infoFilters.type === '2' }"
                      @click="selectType('2')"
                      >{{ $t("pcHome.hongKongMacaoTaiwan") }}</span
                    >
                  </div>
                </div>
                <div class="filter-row">
                  <!-- 关键词： -->
                  <span class="filter-label">{{
                    $t("pcHome.keywordLabel")
                  }}</span>
                  <div class="filter-input-wrapper">
                    <!-- 搜索品牌、料号、产品类型、容量、架构、速率、球位或供应商 -->
                    <el-input
                      v-model="infoFilters.model"
                      :placeholder="$t('pcHome.filterPlaceholder')"
                      size="small"
                      class="filter-input"
                      clearable
                      @keyup.enter.native="sekectList"
                    />
                    <div class="filter-actions">
                      <!-- 重置 -->
                      <el-button size="small" @click="resetFilters">{{
                        $t("pcHome.reset")
                      }}</el-button>
                      <!-- 查询 -->
                      <el-button
                        type="primary"
                        size="small"
                        @click="sekectList"
                        >{{ $t("pcHome.query") }}</el-button
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- 表格内容区域 -->
            <div
              class="table-view-container info-table-scroll"
              ref="tableContainer"
            >
              <!-- 加载中... -->
              <div
                v-if="infoLoading && infoList.length === 0"
                class="loading-container"
                v-loading="infoLoading"
                :element-loading-text="$t('pcHome.loading')"
              ></div>
              <div v-else-if="infoError" class="error-container">
                <!-- 获取数据失败，请刷新重试 -->
                <el-empty :description="$t('pcHome.loadFailedRefresh')" />
                <!-- 重新加载 -->
                <el-button
                  type="primary"
                  size="small"
                  @click="getInfoList"
                  style="margin-top: 20px"
                >
                  {{ $t("pcHome.reload") }}
                </el-button>
              </div>
              <div v-else>
                <el-table
                  :data="infoList"
                  :show-overflow-tooltip="true"
                  @row-click="handleRowClick"
                >
                  <el-table-column width="40" align="center">
                    <template #default="{ row }">
                      <div @click.stop>
                        <el-checkbox
                          :disabled="
                            row.supplierId !== 1 && row.supplierId !== 2
                          "
                          v-model="row.selected"
                          @change="handleCustomSelect(row)"
                        ></el-checkbox>
                      </div>
                    </template>
                  </el-table-column>
                  <!-- 品牌 -->
                  <el-table-column
                    prop="goodsBrand"
                    :label="$t('pcHome.brand')"
                    min-width="60"
                    align="center"
                  />
                  <!-- 料号 -->
                  <el-table-column
                    prop="partNumber"
                    :label="$t('pcHome.partNumber')"
                    min-width="120"
                    align="center"
                  />
                  <!-- 类型 -->
                  <el-table-column
                    prop="productType"
                    :label="$t('pcHome.type')"
                    min-width="60"
                    align="center"
                  />
                  <!-- 容量 -->
                  <el-table-column
                    prop="capacity"
                    :label="$t('pcHome.capacity')"
                    min-width="60"
                    align="center"
                  />
                  <!-- 架构 -->
                  <el-table-column
                    prop="architecture"
                    :label="$t('pcHome.architecture')"
                    min-width="60"
                    align="center"
                  />
                  <!-- 速率 -->
                  <el-table-column
                    prop="speed"
                    :label="$t('pcHome.speed')"
                    min-width="60"
                    align="center"
                  />
                  <!-- 球位 -->
                  <el-table-column
                    prop="ball"
                    :label="$t('pcHome.ballCount')"
                    min-width="60"
                    align="center"
                  />
                  <!-- 单价 -->
                  <el-table-column
                    prop="price"
                    :label="$t('pcHome.unitPrice')"
                    min-width="80"
                    align="center"
                  >
                    <template #default="{ row }">
                      <span style="font-weight: 600; color: #4286f5">{{
                        row.price
                      }}</span>
                    </template>
                  </el-table-column>
                  <!-- 数量 -->
                  <el-table-column
                    prop="goodsQuantity"
                    :label="$t('pcHome.quantity')"
                    min-width="60"
                    align="center"
                  />
                  <!-- 批次 -->
                  <el-table-column
                    prop="manufactureDate"
                    :label="$t('pcHome.batch')"
                    min-width="50"
                    align="center"
                  />
                  <!-- 货所在地 -->
                  <el-table-column
                    prop="deliveryAddress"
                    :label="$t('pcHome.goodsLocation')"
                    min-width="70"
                    align="center"
                  />
                  <!-- 发布时间 -->
                  <el-table-column
                    prop="createTime"
                    :label="$t('pcHome.publishedAt')"
                    min-width="90"
                    align="center"
                  >
                    <template #default="{ row }">
                      {{ row.createTime || $t("auth.sentenceSeparator") }}
                    </template>
                  </el-table-column>
                  <!-- 供应商 -->
                  <el-table-column
                    prop="supplierName"
                    :label="$t('pcHome.supplier')"
                    min-width="80"
                    align="center"
                  />
                </el-table>
                <div
                  v-if="infoLoading && infoList.length > 0"
                  class="info-list-status"
                >
                  <!-- 加载中... -->
                  <van-loading size="20px">{{
                    $t("pcHome.loading")
                  }}</van-loading>
                </div>
                <!-- 我们是有底线的 -->
                <div
                  v-else-if="infoFinished && infoList.length > 0"
                  class="info-list-status"
                >
                  {{ $t("pcHome.endOfList") }}
                </div>
              </div>
            </div>
          </div>
          <!-- 选中项操作弹窗 -->
          <div
            v-if="selectedInfoItems.length > 0"
            style="
              position: fixed;
              bottom: 20px;
              left: 50%;
              transform: translateX(-50%);
              background: #fff;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              padding: 15px 20px;
              box-shadow: 0 2px 15px rgba(0, 0, 0, 0.15);
              z-index: 1000;
              display: flex;
              justify-content: space-between;
              align-items: center;
              min-width: 300px;
              max-width: 400px;
              width: 60%;
            "
          >
            <!-- 已选择 {count} 项 -->
            <div>
              {{
                $t("pcHome.selectedCount", { count: selectedInfoItems.length })
              }}
            </div>
            <div>
              <!-- 删除 -->
              <el-button
                type="danger"
                size="medium"
                style="margin-right: 10px"
                @click="handleDeleteSelected"
                >{{ $t("pcHome.delete") }}</el-button
              >
              <!-- 审核 -->
              <el-button
                v-if="isAudit"
                type="success"
                size="medium"
                @click="handleAuditSelected"
                >{{ $t("pcHome.review") }}</el-button
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <PcHomeSideNav
      @select-featured="scrollToFeatured"
      @back-top="scrollToPageTop"
    />
    <!-- 搜索品牌 -->
    <el-dialog
      :title="$t('pcHome.searchBrand')"
      :visible.sync="showBrandSearchDialog"
      width="420px"
      :close-on-click-modal="false"
      @closed="resetBrandSearchDialog"
    >
      <div style="padding: 8px 20px 0">
        <!-- 请输入品牌名称 -->
        <el-input
          v-model.trim="brandSearchKeyword"
          :placeholder="$t('pcHome.brandNamePlaceholder')"
          clearable
          @input="handleBrandSearch"
          @keyup.enter.native="confirmBrandSearch"
        />
        <div v-if="brandSearchKeyword" class="brand-search-results">
          <!-- 搜索中... -->
          <div v-if="brandSearchLoading" class="brand-search-status">
            {{ $t("pcHome.searching") }}
          </div>
          <template v-else>
            <div
              v-for="brand in brandSearchResults"
              :key="brand.value"
              class="brand-search-item"
              :class="{
                active:
                  selectedSearchBrand &&
                  selectedSearchBrand.text === brand.text,
              }"
              @click="selectSearchBrand(brand)"
            >
              {{ brand.text }}
            </div>
            <!-- 未找到匹配的品牌 -->
            <div
              v-if="brandSearchResults.length === 0"
              class="brand-search-status"
            >
              {{ $t("pcHome.noMatchingBrands") }}
            </div>
          </template>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <!-- 取消 -->
        <el-button @click="cancelBrandSearch">{{
          $t("pcHome.cancel")
        }}</el-button>
        <!-- 确定 -->
        <el-button type="primary" @click="confirmBrandSearch">{{
          $t("pcHome.confirm")
        }}</el-button>
      </span>
    </el-dialog>
    <PcFooter />
  </div>
</template>

<script>
import { Dialog, Toast } from "vant";
import GoodsItem from "@/components/pc/GoodsItem.vue";
import PcFooter from "@/components/pc/PcFooter.vue";
import PcHomeSideNav from "./components/PcHomeSideNav.vue";
import {
  getHomeNav,
  getHomeGoods,
  getBannerAdv,
  getHomeClue,
} from "@/api/home";
import { getUserInfoDetail } from "@/api/user.js";
import { listPendingClue, swPendingFollowList } from "@/api/bizOppor";
import { updateFile } from "@/api/upload";
import { importClue, auditClue, deleteClue } from "@/api/clue";
import { getBrandSearch } from "@/api/picker";
import { Message } from "element-ui";
import { getChatUsers } from "@/api/index";
import { shortcutLabelKeys } from "@/i18n/home/index.js";
import infiniteShortcutCarousel from "@/mixins/infiniteShortcutCarousel";
import chatMessagePrompt from "@/mixins/chatMessagePrompt";
export default {
  name: "HomePage",
  mixins: [infiniteShortcutCarousel, chatMessagePrompt],
  components: {
    GoodsItem,
    PcFooter,
    PcHomeSideNav,
  },
  data() {
    return {
      heroSearchKeyword: "",
      bannerList: [], //轮播
      logoList: [{}, {}], //logo图--要初始化因为我上面直接用了
      brandShowcaseList: [
        {
          name: "三星",
          logo: require("./assets/brands/samsung.png"),
          isFallback: true,
        },
        {
          name: "西部数据",
          logo: require("./assets/brands/western-digital.png"),
          isFallback: true,
        },
        {
          name: "慧荣科技",
          logo: require("./assets/brands/silicon-motion.png"),
          isFallback: true,
        },
        {
          name: "忆恒创源",
          logo: require("./assets/brands/starblaze.png"),
          isFallback: true,
        },
        {
          name: "联芸科技",
          logo: require("./assets/brands/maxio.png"),
          isFallback: true,
        },
        {
          name: "铠侠",
          logo: require("./assets/brands/kioxia.png"),
          isFallback: true,
        },
        {
          name: "佰维存储",
          logo: require("./assets/brands/biwin.png"),
          isFallback: true,
        },
        {
          name: "江波龙",
          logo: require("./assets/brands/longsys.png"),
          isFallback: true,
        },
        {
          name: "海力士",
          logo: require("./assets/brands/sk-hynix.png"),
          isFallback: true,
        },
        {
          name: "希捷",
          logo: require("./assets/brands/seagate.png"),
          isFallback: true,
        },
        {
          name: "长江存储",
          logo: require("./assets/brands/ymtc.jpg"),
          isFallback: true,
        },
        {
          name: "英韧科技",
          logo: require("./assets/brands/innogrit.jpg"),
          isFallback: true,
        },
      ],
      brandShowcaseLoading: false,
      navList: [], //导航
      proList: [], //商品
      advImg: null,
      params: {
        pageSize: 30,
        pageNum: 0,
        pageNum: 1,
        which: 1,
        position: "HOME",
        status: 0,
        goodsType: null,
      },
      tabList: [
        { title: "供应", type: 1 },
        { title: "求购", type: 2 },
      ],
      list: [],
      active: 0,
      serachShow: false, //搜索查询状态刷新状态
      checkMore: true,
      refreshing: false, //刷新状态默认值
      loading: false,
      finished: false,
      error: false,
      scrollContainer: null,
      hasNewClue: null, //是否有新订单
      hasUnreadMessage: false, //是否有未读消息（谈判人）
      userBusinessType: null, // 用户业务类型
      activeTab: 0,
      // 视图模式：grid(图片视图) 或 table(表格视图)
      viewMode: "grid",
      // 信息列表数据
      infoList: [],
      // 信息列表加载状态
      infoLoading: false,
      // 下一页请求锁，防止停留在底部时重复触发分页请求
      infoLoadingMore: false,
      // 信息列表错误状态
      infoError: false,
      // 信息列表是否加载完成
      infoFinished: false,
      // 信息列表筛选条件
      infoFilters: {
        brand: "",
        model: "",
        type: "",
        supplier: "",
        pageNum: 1,
        pageSize: 30,
        clueType: 1,
      },
      brandList: [],
      showBrandSearchDialog: false,
      brandSearchKeyword: "",
      brandSearchResults: [],
      selectedSearchBrand: null,
      brandSearchLoading: false,
      brandSearchRequestId: 0,
      // 选中的信息列表项
      selectedInfoItems: [],
      // 批量上传相关
      showImportDialog: false,
      importFileList: [],
      isAudit: false,
    };
  },
  computed: {
    // ...mapState({
    //   userInfo: (state) => state.user.userDetails,
    // }),
    visibleBrands() {
      return this.brandList.slice(0, 11);
    },
    brandShowcaseItems() {
      return this.brandShowcaseList.slice(0, 12);
    },
    heroBgMaxHeight() {
      const height = this.logoList[1]?.mark || this.logoList[1]?.remark || 0;
      return typeof height === "number" ? `${height}px` : height;
    },
    // 标签页数据，根据用户角色动态显示
    tabs() {
      let baseTabs = [];
      // 只有businessType == 10时才显示两个信息标签页
      if (this.userBusinessType == 10) {
        baseTabs = [
          // 成交信息
          { title: this.$t("pcHome.tradeInfo") },
          // 供需信息
          { title: this.$t("pcHome.supplyDemandInfo") },
        ];
      }
      return baseTabs;
    },
  },
  mounted() {
    this.scrollContainer = this.$refs.pchome;
    // 使用Vue的事件监听方式
    this.scrollContainer.addEventListener("scroll", this.updateLayout);
    this.scrollContainer.addEventListener("scroll", this.handleInfoScroll, {
      passive: true,
    });
    // 初始化搜索框位置
    this.updateLayout();
    // 加载品牌列表
    this.loadBrandList();
  },
  beforeDestroy() {
    // 移除滚动事件监听
    if (this.scrollContainer) {
      console.log("移除滚动事件监听");
      this.scrollContainer.removeEventListener("scroll", this.updateLayout);
      this.scrollContainer.removeEventListener("scroll", this.handleInfoScroll);
    }
    this.$emit("home-nav-compact-change", false);
  },
  methods: {
    localizedShortcutLabel(item) {
      const key = shortcutLabelKeys[item?.text];
      // 动态简中示例：按 home/index.js 的 shortcutLabelKeys 取快捷入口名称；完整中文见对应 zh-CN.js，示例不代表固定返回值
      // pcHome.shortcut.supplyDemand：供需
      // pcHome.shortcut.supply：供应
      // pcHome.shortcut.demand：需求
      // pcHome.shortcut.selfOperated：自营
      // pcHome.shortcut.featured：精选
      // pcHome.shortcut.bidding：竞标
      return key ? this.$t(key) : item?.text || "";
    },
    scrollToFeatured() {
      this.activeTab = 0;
      this.$nextTick(() => {
        const pageContainer = this.scrollContainer || this.$refs.pchome;
        const target = this.$el.querySelector(".goods-section");
        if (!pageContainer || !target) return;

        const pageRect = pageContainer.getBoundingClientRect();
        const header = document.querySelector(".layout_top");
        const headerHeight = header ? header.getBoundingClientRect().height : 0;
        const targetTop =
          pageContainer.scrollTop +
          target.getBoundingClientRect().top -
          pageRect.top -
          headerHeight -
          16;
        pageContainer.scrollTo({
          top: Math.max(0, targetTop),
          behavior: "smooth",
        });
      });
    },
    scrollToPageTop() {
      const pageContainer = this.scrollContainer || this.$refs.pchome;
      if (!pageContainer) return;

      pageContainer.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    // 更新顶部导航栏位置
    updateLayout() {
      const trigger = this.$el.querySelector(".brand-showcase");
      const layout = document.querySelector(".layout_top");
      const pageContainer = this.scrollContainer || this.$refs.pchome;
      const triggerTop = trigger
        ? trigger.getBoundingClientRect().top
        : Number.POSITIVE_INFINITY;
      const navBottom = layout ? layout.getBoundingClientRect().bottom : 0;
      // 首屏图片异步加载前，品牌区会短暂出现在导航栏上方。
      // 在页面确实发生滚动前始终保持首页导航的展开态，避免把加载中的
      // 临时布局误判成紧凑态；图片加载完成后会通过 @load 再次校准。
      const hasScrolled = Boolean(pageContainer && pageContainer.scrollTop > 0);
      const isCompact = hasScrolled && triggerTop <= navBottom;
      this.isScroll = isCompact;
      this.$emit("home-nav-compact-change", isCompact);
    },
    // 首页滚动到底部时加载下一页供需信息
    async handleInfoScroll(event) {
      const pageContainer = event.currentTarget;
      if (
        !pageContainer ||
        this.activeTab !== 1 ||
        this.userBusinessType != 10 ||
        this.infoList.length === 0 ||
        this.infoLoading ||
        this.infoLoadingMore ||
        this.infoFinished ||
        this.infoError
      ) {
        return;
      }

      const scrollBottom =
        pageContainer.scrollHeight -
        pageContainer.scrollTop -
        pageContainer.clientHeight;
      if (scrollBottom > 1) return;

      const currentPage = this.infoFilters.pageNum;
      this.infoLoadingMore = true;
      this.infoLoading = true;
      this.infoFilters.pageNum = currentPage + 1;
      try {
        const loaded = await this.getInfoList(true);
        if (!loaded) {
          this.infoFilters.pageNum = currentPage;
        }
      } finally {
        // 必须等本次接口完全结束后，才允许下一次滚动加载
        this.infoLoadingMore = false;
      }
    },
    // 首页只上报关键词，登录判断和页面跳转统一由公共布局处理。
    onProductSearch(value) {
      this.$emit("product-search", value);
    },
    openAiAssistant() {
      this.$router.push("/pc/myAi");
    },
    isLogin() {
      return this.$store.getters.token;
    },
    async getUserInfoDetail() {
      const response = await getUserInfoDetail();
      if (response.code === 200) {
        const { businessType } = response.data.userInfo;
        this.userBusinessType = businessType; // 保存用户业务类型
        // 设置默认视图模式：谈判人默认表格视图，其他默认图片视图
        this.viewMode = businessType == 10 ? "table" : "grid";
        // 谈判人角色|| 商务角色
        if (businessType == 10 || businessType == 20) {
          this.needShowTip(businessType);
        }
        // 如果是谈判人，获取信息列表
        if (businessType == 10) {
          this.getInfoList();
        }
      }
    },
    async needShowTip(type) {
      // 确保类型一致，避免 switch (===) 匹配失败
      let businessType = Number(type);
      switch (businessType) {
        case 10:
          // 谈判人-未读消息，检查 store 中的 unreadCount
          this.hasUnreadMessage = this.$store.state.user.unreadCount > 0;
          break;
        case 20:
          // 商务-新订单列表
          this.hasNewClue = await swPendingFollowList();
          break;
      }
      // 谈判人有未读消息时弹出提示
      if (businessType == 10 && this.hasUnreadMessage) {
        this.showChatMessagePrompt();
      }
      // 商务有新订单时弹出提示
      if (
        businessType == 20 &&
        this.hasNewClue.rows &&
        this.hasNewClue.rows.length
      ) {
        sessionStorage.setItem("hasTip", 1);
        Dialog.confirm({
          // 您有新订单!
          title: this.$t("pcHome.newOrders"),
          // 去接单
          confirmButtonText: this.$t("pcHome.acceptOrder"),
          confirmButtonColor: "#4689F2",
        })
          .then(() => {
            this.$router.push({
              path: "/follow/swPendingFollow",
            });
          })
          .catch(() => {
            // on cancel
          });
      }
    },
    onJump(item) {
      if (item.path) {
        if (item.text == "供应" || item.text == "需求") {
          this.$router.push({
            path: item.path,
            query: { active: item.text == "供应" ? 0 : 1 },
          });
        } else {
          this.$router.push(item.path);
        }
      } else if (item.goodsCode) {
        this.$router.push(`/prodetail/${item.goodsCode}`);
      }
    },
    // 处理表格行点击事件
    handleRowClick(row) {
      // 跳转到货源详情页面，带着id
      this.$router.push({
        path: "/clue/clueDetail",
        query: { id: row.id },
      });
    },
    // 处理品牌栏点击事件
    handleBrandClick(row) {
      // 阻止事件冒泡，不让品牌栏触发行点击事件
      console.log("品牌栏被点击:", row.goodsBrand);
    },
    getBrandName(item) {
      // 品牌
      return item?.name || item?.text || this.$t("pcHome.brand");
    },
    getBrandInitial(item) {
      return (
        this.getBrandName(item).trim().slice(0, 1).toUpperCase() ||
        // 品
        this.$t("pcHome.brandInitial")
      );
    },
    openBrand(item) {
      if (item?.id && !item.isFallback) {
        this.$router.push({
          path: "/pc/brand/detail",
          query: { id: item.id },
        });
        return;
      }
      this.$router.push({
        path: "/pc/clue",
        query: { brand: this.getBrandName(item) },
      });
    },
    goBrandHall() {
      this.$router.push("/pc/brand");
    },
    selectBrand(brand) {
      this.infoFilters.brand = brand;
      this.infoFilters.pageNum = 1;
      this.infoList = [];
      this.getInfoList();
    },
    openBrandSearchDialog() {
      this.showBrandSearchDialog = true;
    },
    handleBrandSearch(keyword) {
      this.selectedSearchBrand = null;
      const name = keyword.trim();
      if (!name) {
        this.brandSearchResults = [];
        this.brandSearchLoading = false;
        return;
      }

      const requestId = ++this.brandSearchRequestId;
      this.brandSearchLoading = true;
      getBrandSearch({ name })
        .then((res) => {
          if (requestId !== this.brandSearchRequestId) return;
          this.brandSearchResults =
            res && res.code === 200 && Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          if (requestId === this.brandSearchRequestId) {
            this.brandSearchResults = [];
          }
        })
        .finally(() => {
          if (requestId === this.brandSearchRequestId) {
            this.brandSearchLoading = false;
          }
        });
    },
    selectSearchBrand(brand) {
      this.selectedSearchBrand = brand;
      this.brandSearchKeyword = brand.text;
    },
    confirmBrandSearch() {
      // clueList 的 brand 参数按品牌文字进行模糊查询；可直接输入，也可从联想结果回填。
      this.infoFilters.brand = this.brandSearchKeyword;
      this.infoFilters.pageNum = 1;
      this.infoList = [];
      this.showBrandSearchDialog = false;
      this.getInfoList();
    },
    cancelBrandSearch() {
      this.showBrandSearchDialog = false;
    },
    resetBrandSearchDialog() {
      this.brandSearchRequestId += 1;
      this.brandSearchKeyword = "";
      this.brandSearchResults = [];
      this.selectedSearchBrand = null;
      this.brandSearchLoading = false;
    },
    selectType(type) {
      this.infoFilters.type = type;
      this.infoFilters.pageNum = 1;
      this.infoList = [];
      this.getInfoList();
    },
    loadBrandList() {
      getBrandSearch({ name: "" })
        .then((res) => {
          this.brandList =
            res && res.code == 200 && Array.isArray(res.data) ? res.data : [];
          this.$nextTick(this.updateLayout);
        })
        .catch(() => {
          this.brandList = [];
        });
    },
    // 重置筛选条件
    resetFilters() {
      this.infoFilters = {
        brand: "",
        model: "",
        type: "",
        supplier: "",
        pageNum: 1,
        pageSize: 30,
      };
      this.infoList = [];
      this.getInfoList();
    },
    sekectList() {
      this.infoList = [];
      this.infoFilters.pageNum = 1;
      this.getInfoList();
    },
    // 查询banner或者adv【type:1logo，2banner，3special，4adv】
    queryBannerAdv(params, callback) {
      getBannerAdv(params)
        .then((resp) => {
          callback(resp);
        })
        .catch((error) => {
          console.error("Error fetching banner data:", error);
        });
    },
    // 查询商品列表接口
    // queryList() {
    //   getHomeGoods(this.params).then((res) => {
    //     this.loading = false;
    //     if (res.code == 200 && res.data) {
    //       if (res.data.length == 0) {
    //         this.finished = true;
    //       } else {
    //         this.finished = false;
    //         this.list = this.list.concat(res.data);
    //       }
    //     } else {
    //       this.error = true;
    //     }
    //   });
    // },
    // 绘制文字图片
    drawTextCanvas(brand, name) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const width = (canvas.width = 600);
      const height = (canvas.height = 600);
      // 背景黑色
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);
      // 文字白色
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      // 文字区域宽度70%
      const textWidth = width * 0.7;
      // 调整字号以适应宽度
      const adjustFontSize = (text, maxWidth) => {
        let fontSize = 48;
        ctx.font = `${fontSize}px Arial`;
        while (ctx.measureText(text).width > maxWidth && fontSize > 12) {
          fontSize -= 2;
          ctx.font = `${fontSize}px Arial`;
        }
        return fontSize;
      };
      // 绘制品牌
      if (brand) {
        const brandFontSize = adjustFontSize(brand, textWidth);
        ctx.font = `${brandFontSize}px Arial`;
        ctx.fillText(brand, width / 2, height / 2 - 30);
      }
      // 绘制商品名称
      if (name) {
        const nameFontSize = adjustFontSize(name, textWidth);
        ctx.font = `${nameFontSize}px Arial`;
        ctx.fillText(name, width / 2, height / 2 + 30);
      }
      return canvas.toDataURL("image/png");
    },
    queryList() {
      getHomeGoods(this.params).then((res) => {
        this.loading = false;
        this.finished = true;
        if (res.code == 200 && res.data) {
          this.list = res.data ?? [];
          if (this.list.length == 0) return;
          this.list.forEach((item) => {
            // item.tags = "新品，热销，爆款";
            if (item.tags) {
              item.handleTags = item.tags
                .replace(/，/g, ",")
                .split(",")
                .filter((item) => item.trim() !== "");
              // 步骤1：替换中文逗号为英文逗号 → 步骤2：按英文逗号拆分 → 步骤3：过滤空字符串（避免首尾逗号问题）
            } else {
              item.handleTags = [];
            }
            // 如果没有图片，生成文字图片
            if (!item.goodsImage || item.goodsImage === "") {
              item.goodsImage = this.drawTextCanvas(
                item.goodsBrand || "",
                // 无料号
                item.goodsNum || this.$t("pcHome.noPartNumber"),
              );
            }
          });
        }
      });
    },
    //页面加载
    onLoad() {
      this.loading = false;
      // 模拟更新数据
      if (this.checkMore) {
        this.loading = true;
        // 如果有新数据
        setTimeout(() => {
          this.queryList();
        }, 300);
      } else {
        // 如果没有新数据
        this.finished = true;
      }
    },
    // onLoad() {
    //   this.loading = true;
    //   this.params.pageNum = this.params.pageNum + 1;
    //   this.queryList();
    // },

    // tabs分类
    // supeActive() {
    //   if (this.active == 0) {
    //     this.params.goodsType = 1;
    //   }
    //   if (this.active == 1) {
    //     this.params.goodsType = 2;
    //   }
    // },
    onTab(index) {
      this.active = index;
      this.params.pageNum = 1;
      this.serachShow = true;
      // this.supeActive();
      this.queryList();
    },
    // onTab(index) {
    //   this.active = index;
    //   this.serachShow = true;
    //   this.supeActive();
    //   this.onLoad();
    // },
    // 获取信息列表数据
    async getInfoList(isLoadMore = false) {
      // 只有谈判人角色才请求信息列表
      if (this.userBusinessType == 10) {
        if (!isLoadMore) {
          // 首次、筛选和刷新查询始终从第一页开始
          this.infoFilters.pageNum = 1;
          this.infoLoading = true;
          this.infoError = false;
          this.infoFinished = false;
          if (this.$refs.tableContainer) {
            this.$refs.tableContainer.scrollTop = 0;
          }
        }
        try {
          const response = await getHomeClue(this.infoFilters);
          // console.log('接口返回数据:', response);
          if (response.code === 200) {
            // 处理接口返回的数据，确保字段名正确
            const processedData = response.rows.map((item) => {
              if (item.supplierId == 1) this.isAudit = true;
              // console.log('原始数据项:', item);
              // 格式化时间
              let formattedCreateTime = "";
              if (item.createTime) {
                const date = new Date(item.createTime);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const hours = String(date.getHours()).padStart(2, "0");
                const minutes = String(date.getMinutes()).padStart(2, "0");
                const seconds = String(date.getSeconds()).padStart(2, "0");
                formattedCreateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
              }
              return {
                id: item.id || item.clueId || "",
                partNumber: item.partNumber || "",
                goodsBrand: item.goodsBrand || "",
                productType: item.productType || "",
                capacity: item.capacity || "",
                architecture: item.architecture || "",
                speed: item.speed || "",
                ball: item.ball || "",
                price:
                  item.goodsPrice !== null && item.goodsPrice !== undefined
                    ? `${item.goodsPrice}${
                        item.goodsPriceUnit === 0
                          ? "CNY"
                          : item.goodsPriceUnit === 10
                            ? "USD"
                            : ""
                      }`
                    : "",
                goodsQuantity: item.goodsQuantity || "",
                manufactureDate: item.manufactureDate || "",
                deliveryAddress: item.deliveryAddress || "",
                usageScenario: item.usageScenario || "",
                supplierName: item.supplierName || "",
                supplierId: item.supplierId || "",
                selected: false,
                createTime: formattedCreateTime,
              };
            });
            // console.log('处理后的数据:', processedData);
            if (isLoadMore) {
              this.infoList.push(...processedData);
            } else {
              this.infoList = processedData;
            }
            // 判断是否还有更多数据
            if (processedData.length < this.infoFilters.pageSize) {
              this.infoFinished = true;
            }
            return true;
          } else {
            this.infoError = true;
            return false;
          }
        } catch (error) {
          console.error("获取信息列表失败:", error);
          this.infoError = true;
          return false;
        } finally {
          this.infoLoading = false;
        }
      }
      return false;
    },
    // 处理文件上传
    async handleFileUpload(file) {
      file.status = "uploading";
      // 上传中...
      file.message = this.$t("pcHome.uploading");
      const formData = new FormData();
      formData.append("file", file.file);
      try {
        const resp = await updateFile(formData);
        if (resp.code == 200) {
          file.status = "success";
          // 上传成功
          file.message = this.$t("pcHome.uploadSuccess");
          file.url = resp.url;
        } else {
          // 上传失败
          Message({ message: this.$t("pcHome.uploadFailed"), type: "error" });
          file.status = "failed";
          // 上传失败
          file.message = this.$t("pcHome.uploadFailed");
        }
      } catch (err) {
        // 上传失败
        Message({ message: this.$t("pcHome.uploadFailed"), type: "error" });
        file.status = "failed";
        // 上传失败
        file.message = this.$t("pcHome.uploadFailed");
      }
    },
    // 删除文件
    beforeDeleteFile() {
      return true;
    },
    // 提交导入
    submitImport() {
      if (this.importFileList.length === 0) {
        // 请选择要上传的文件
        Message.warning(this.$t("pcHome.chooseFileFirst"));
        return;
      }

      const fileItem = this.importFileList[0];
      if (fileItem.status !== "success") {
        // 文件上传中，请等待上传完成
        Message.warning(this.$t("pcHome.waitForUpload"));
        return;
      }

      const formData = new FormData();
      formData.append("file", fileItem.file);

      importClue(formData).then((res) => {
        if (res.code == 200) {
          // 导入成功
          Toast.success(this.$t("pcHome.importSuccess"));
          this.showImportDialog = false;
          this.importFileList = [];
          // 重新加载信息列表
          this.getInfoList();
        } else {
          // 导入失败
          Toast.error(res.msg || this.$t("pcHome.importFailed"));
        }
      });
    },
    // 处理取消操作
    handleCancel() {
      this.showImportDialog = false;
      this.importFileList = [];
    },
    // 处理信息列表项选择
    handleCustomSelect(row) {
      if (row.selected) {
        // Add to selected items
        this.selectedInfoItems.push(row);
      } else {
        // Remove from selected items
        this.selectedInfoItems = this.selectedInfoItems.filter(
          (item) => item.id !== row.id,
        );
      }
    },
    // 处理删除选中项
    handleDeleteSelected() {
      // 获取选中项的ids
      const ids = this.selectedInfoItems.map((item) => item.id);
      if (ids.length === 0) return;

      // 二次确认
      Dialog.confirm({
        // 删除确认
        title: this.$t("pcHome.deleteConfirmTitle"),
        // 是否删除这{count}条数据？
        message: this.$t("pcHome.deleteConfirmMessage", {
          count: this.selectedInfoItems.length,
        }),
        // 确定
        confirmButtonText: this.$t("pcHome.confirm"),
        // 取消
        cancelButtonText: this.$t("pcHome.cancel"),
      })
        .then(() => {
          // 调用删除接口
          deleteClue({ ids })
            .then((resp) => {
              if (resp.code === 200) {
                // 删除成功
                Toast.success(this.$t("pcHome.deleteSuccess"));
                // 重置所有选中状态
                this.infoList.forEach((item) => {
                  item.selected = false;
                });
                this.selectedInfoItems = [];
                // 重新加载信息列表
                this.getInfoList();
              }
            })
            .catch((error) => {
              console.error("删除失败:", error);
              // 删除失败，请稍后重试
              Toast.fail(this.$t("pcHome.deleteFailed"));
            });
        })
        .catch(() => {
          // 取消删除
        });
    },
    // 处理审核选中项
    handleAuditSelected() {
      // 获取选中项的ids
      const ids = this.selectedInfoItems.map((item) => item.id);
      if (ids.length === 0) return;

      // 二次确认
      Dialog.confirm({
        // 审核确认
        title: this.$t("pcHome.reviewConfirmTitle"),
        // 是否审核这{count}条数据？
        message: this.$t("pcHome.reviewConfirmMessage", {
          count: this.selectedInfoItems.length,
        }),
        // 确定
        confirmButtonText: this.$t("pcHome.confirm"),
        // 取消
        cancelButtonText: this.$t("pcHome.cancel"),
      })
        .then(() => {
          // 调用审核接口
          auditClue({ ids }).then((resp) => {
            if (resp.code === 200) {
              // 审核成功
              Message.success(this.$t("pcHome.reviewSuccess"));
              // 重置所有选中状态
              this.infoList.forEach((item) => {
                item.selected = false;
              });
              this.selectedInfoItems = [];
              // 重新加载信息列表
              this.getInfoList();
              this.queryList();
            }
          });
        })
        .catch(() => {
          // 取消审核
        });
    },
  },
  async created() {
    // 检查用户登录状态，获取用户信息
    if (this.isLogin()) {
      this.getUserInfoDetail();
    }
    this.queryBannerAdv(
      { which: 1, type: 1, position: "HOME", status: 0 },
      (res) => {
        if (res.code == 200 && res.data && res.data.length > 0) {
          this.logoList = res.data.sort((a, b) => a.sort - b.sort);
        }
      },
    );
    this.queryBannerAdv(
      { which: 1, type: 2, position: "HOME", status: 0 },
      (res) => {
        if (res.code == 200 && res.data && res.data.length > 0) {
          this.bannerList = res.data;
        }
      },
    );
    this.queryBannerAdv(
      { which: 1, type: 4, position: "HOME", status: 0 },
      (res) => {
        if (res.code == 200 && res.data && res.data.length > 0) {
          this.advImg = res.data[0].image;
        }
      },
    );
    // 加载商品列表
    this.onLoad();
    getHomeNav({ pageId: 0, which: 1, position: "HOME", status: 0 }).then(
      (resp) => {
        this.navList = resp.data;
        this.$nextTick(() => {
          this.updateShortcutScrollState();
          this.updateLayout();
        });
      },
    );
  },
};
</script>

<style lang="less" scoped>
.home {
  --home-content-shift: clamp(0px, calc(12.5vw - 160px), 80px);
  width: 100%;
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: #f4f4f6;
}

.hero-banner {
  width: 100%;
  position: relative;
  margin-top: -180px;
  overflow: hidden;

  > img {
    display: block;
    width: 100%;
    height: auto;
    object-fit: cover;
    transform: translateY(180px);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
    height: 180px;
    background: linear-gradient(
      to bottom,
      rgba(244, 244, 246, 0) 0%,
      rgba(244, 244, 246, 0.72) 68%,
      #f4f4f6 100%
    );
    pointer-events: none;
  }

  .hero-search-area {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 14px;
    width: min(820px, calc(100% - 80px));
    transform: translate(-50%, calc(-50% + 50px - var(--home-content-shift)));
  }

  .hero-search-main {
    position: relative;
    display: flex;
    flex: 1;
    min-width: 0;
  }

  .hero-search-logo {
    position: absolute;
    bottom: calc(100% + 36px);
    left: 50%;
    display: block;
    width: 430px;
    max-width: 70%;
    height: auto;
    object-fit: contain;
    transform: translateX(-50%);
  }

  .hero-search-shell {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    height: 56px;
    padding: 5px 6px 5px 24px;
    border: 1px solid rgba(255, 255, 255, 0.9);
    border-radius: 30px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 6px 16px rgba(32, 65, 116, 0.08);
    box-sizing: border-box;
    backdrop-filter: blur(8px);
  }

  .hero-value-strip {
    position: absolute;
    top: calc(100% + 12px);
    left: 50%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 88%;
    color: #69717d;
    transform: translateX(-50%);
  }

  .hero-value-item {
    display: flex;
    flex: 0 0 auto;
    min-width: 0;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 13px;
    line-height: 20px;
    white-space: nowrap;

    img {
      flex: 0 0 18px;
      width: 18px;
      height: 18px;
      object-fit: contain;
    }
  }

  .hero-search-input {
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0 16px 0 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: #25324a;
    font-size: 16px;

    &::placeholder {
      color: #9aa4b5;
    }

    &::-webkit-search-cancel-button {
      cursor: pointer;
    }
  }

  .hero-search-button,
  .hero-ai-button {
    height: 46px;
    border-radius: 24px;
    font-size: 15px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition:
      transform 0.2s ease,
      background-color 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-1px);
    }

    &:focus-visible {
      outline: 3px solid rgba(66, 134, 245, 0.24);
      outline-offset: 2px;
    }
  }

  .hero-search-button {
    flex: 0 0 118px;
    border: 0;
    background: #4286f5;
    color: #ffffff;

    &:hover {
      background: #3277e8;
      box-shadow: 0 8px 18px rgba(66, 134, 245, 0.24);
    }
  }

  .hero-ai-button {
    flex: 0 0 106px;
    border: 1px solid rgba(66, 134, 245, 0.24);
    background: rgba(255, 255, 255, 0.94);
    color: #357ae8;
    box-shadow: 0 12px 28px rgba(32, 65, 116, 0.12);

    &:hover {
      background: #ffffff;
      box-shadow: 0 12px 28px rgba(66, 134, 245, 0.18);
    }
  }
}

// 下列内容的整体容器
.main-container {
  width: @pc-container-width;
  position: relative;
  z-index: 3;
  margin: calc(-278px - var(--home-content-shift)) auto 0;
  display: flex;
  flex-direction: column;
  padding: 0 0 40px;

  @media (max-width: 1400px) {
    width: calc(100% - 200px);
  }

  .brand-showcase {
    width: 100%;
    box-sizing: border-box;
    margin-bottom: 24px;
    padding: 0;
    border: 0;
    background: transparent;

    .brand-showcase-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 14px;

      .brand-showcase-heading {
        h2 {
          margin: 0;
          color: #20242c;
          font-size: 18px;
          font-weight: 600;
          line-height: 1.3;
        }
      }

      .brand-showcase-more {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 0;
        border: 0;
        background: transparent;
        color: #4286f5;
        font-size: 13px;
        line-height: 24px;
        cursor: pointer;

        i {
          font-size: 12px;
          transition: transform 0.2s ease;
        }

        &:hover {
          color: #256cd8;

          i {
            transform: translateX(2px);
          }
        }
      }
    }

    .brand-showcase-grid {
      display: grid;
      grid-template-columns: repeat(6, minmax(0, 1fr));
      gap: 12px;

      .brand-showcase-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        min-width: 0;
        padding: 0;
        border: 0;
        background: transparent;
        color: inherit;
        text-align: center;
        cursor: pointer;

        .brand-showcase-card {
          width: 100%;
          height: 82px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          padding: 13px 12px;
          border: 1px solid #edf0f5;
          border-radius: 13px;
          background: #ffffff;
          transition:
            transform 0.2s ease,
            border-color 0.2s ease,
            box-shadow 0.2s ease;

          .brand-showcase-logo {
            width: 100%;
            height: 54px;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            color: #4286f5;
            font-size: 18px;
            font-weight: 600;

            img {
              width: auto;
              height: auto;
              max-width: 124px;
              max-height: 44px;
              object-fit: contain;
            }
          }
        }

        &:hover {
          .brand-showcase-card {
            border-color: rgba(66, 134, 245, 0.34);
            box-shadow: 0 8px 18px rgba(32, 65, 116, 0.08);
            transform: translateY(-2px);
          }
        }
      }
    }

    .brand-showcase-state {
      height: 78px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 13px;
      background: #f8fafc;
      color: #9aa1ad;
      font-size: 13px;
    }
  }

  .shortcut-wrapper {
    width: 100%;
    box-sizing: border-box;
    margin-top: 0;
    margin-bottom: 12px;

    .shortcut-container {
      position: relative;
      padding: 0;

      .shortcut-viewport {
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
        scroll-behavior: smooth;
        scrollbar-width: none;
        overscroll-behavior-inline: contain;

        &::-webkit-scrollbar {
          display: none;
        }

        &.shortcut-fade-left {
          -webkit-mask-image: linear-gradient(
            to right,
            transparent 0,
            #000 48px,
            #000 100%
          );
          mask-image: linear-gradient(
            to right,
            transparent 0,
            #000 48px,
            #000 100%
          );
        }

        &.shortcut-fade-right {
          -webkit-mask-image: linear-gradient(
            to right,
            #000 0,
            #000 calc(100% - 48px),
            transparent 100%
          );
          mask-image: linear-gradient(
            to right,
            #000 0,
            #000 calc(100% - 48px),
            transparent 100%
          );
        }

        &.shortcut-fade-left.shortcut-fade-right {
          -webkit-mask-image: linear-gradient(
            to right,
            transparent 0,
            #000 48px,
            #000 calc(100% - 48px),
            transparent 100%
          );
          mask-image: linear-gradient(
            to right,
            transparent 0,
            #000 48px,
            #000 calc(100% - 48px),
            transparent 100%
          );
        }
      }

      .shortcut-track {
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: calc((100% - 60px) / 6.25);
        gap: 12px;
        width: 100%;
        padding: 10px 0 16px;

        .shortcut-item {
          width: 100%;
          height: 142px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          border: 1px solid rgba(225, 230, 238, 0.78);
          border-radius: 14px;
          background: #ffffff;
          box-shadow: 0 4px 12px rgba(32, 65, 116, 0.035);
          cursor: pointer;
          padding: 16px 12px;
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease,
            border-color 0.2s ease;

          .shortcut-icon {
            width: 52px;
            height: 52px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 10px;

            img {
              width: 52px;
              height: 52px;
              object-fit: contain;
            }
          }

          .shortcut-text {
            max-width: 100%;
            overflow: hidden;
            color: #4f5663;
            font-size: 15px;
            text-align: center;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          &:hover {
            border-color: rgba(66, 134, 245, 0.28);
            box-shadow: 0 8px 18px rgba(32, 65, 116, 0.08);
            transform: translateY(-2px);

            .shortcut-text {
              color: #4689f2;
            }
          }
        }
      }

      .shortcut-scroll-button {
        position: absolute;
        top: 50%;
        z-index: 2;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: 0;
        border-radius: 50%;
        background: rgba(75, 82, 94, 0.58);
        color: #ffffff;
        box-shadow: 0 3px 10px rgba(32, 39, 52, 0.1);
        cursor: pointer;
        transform: translateY(-50%);
        transition:
          opacity 0.2s ease,
          background-color 0.2s ease;

        &:hover:not(:disabled) {
          background: #4286f5;
        }

        &:disabled {
          opacity: 0.28;
          cursor: default;
        }

        &.shortcut-scroll-button-left {
          left: -44px;
        }

        &.shortcut-scroll-button-right {
          right: 8px;
        }
      }
    }
  }
}

.import-dialog {
  padding: 28px 32px 32px;
  text-align: center;

  .import-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0 0 8px 0;
  }

  .import-desc {
    font-size: 13px;
    color: #999;
    margin: 0 0 24px 0;
  }

  .upload-trigger {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 24px;
    background: #f8fafc;
    border: 2px dashed #e2e8f0;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.25s ease;

    &:hover {
      border-color: #4286f5;
      background: #f0f7ff;

      svg {
        stroke: #4286f5;
      }
    }

    .upload-text {
      margin-top: 12px;
      font-size: 13px;
      color: #666;
    }
  }

  .import-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 24px;
  }
}

:deep(.van-popup--center) {
  background: #ffffff;
  border-radius: 16px;
  position: fixed !important;
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
  right: auto !important;
  bottom: auto !important;
  margin: 0 !important;
}

:deep(.van-popup__close-icon) {
  color: #999;
}

.goods-section {
  margin-top: 12px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tab-nav {
  display: flex;
  gap: 4px;
  background: #f5f7fa;
  padding: 4px;
  border-radius: 10px;
}

.tab-nav-item {
  padding: 10px 24px;
  font-size: 15px;
  cursor: pointer;
  border-radius: 8px;
  color: #666;
  font-weight: 500;
  transition: all 0.25s ease;

  &:hover {
    color: #4689f2;
    background: rgba(70, 137, 242, 0.06);
  }

  &.active {
    color: #ffffff;
    font-weight: 600;
    background: linear-gradient(135deg, #4689f2 0%, #357ae8 100%);
    box-shadow: 0 2px 8px rgba(70, 137, 242, 0.3);
  }
}

.view-toggle {
  display: flex;
  gap: 4px;
  background: #f5f7fa;
  padding: 4px;
  border-radius: 10px;
}

.view-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  color: #999;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    color: #666;
    background: rgba(0, 0, 0, 0.04);
  }

  &.active {
    color: #333;
    background: #ffffff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
}

.section-content {
  min-height: 400px;
}

// 商品列表样式
.goods-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  column-gap: 20px;
  row-gap: 12px;
  margin-bottom: 12px;

  > .goods-item-pc {
    margin-bottom: 0;
  }
}

.table-view-container {
  margin-top: 20px;
  background: #ffffff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
  overflow-x: hidden;
}

.goods-table {
  :deep(.el-table__row) {
    cursor: pointer;
  }

  :deep(.goods-image-cell) {
    .cell {
      padding: 0;
      text-overflow: clip;
    }

    img {
      display: block;
      margin: 0 auto;
    }
  }
}

.info-table-scroll {
  box-sizing: border-box;
  height: auto;
  overflow-y: visible;
}

.info-list-status {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  color: #969799;
  font-size: 13px;
}

:deep(.el-table) {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: none;

  .el-table__header-wrapper {
    th {
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
      font-weight: 600;
      color: #475569;
      font-size: 13px;
      text-align: center;
      padding: 14px 8px;
      border-bottom: 2px solid #e2e8f0;

      .cell {
        white-space: nowrap;
      }
    }
  }

  .el-table__body-wrapper {
    td {
      font-size: 13px;
      color: #334155;
      padding: 14px 8px;
      text-align: center;
      border-bottom: 1px solid #f1f5f9;
      vertical-align: middle;

      .cell {
        white-space: normal;
        word-break: break-all;
      }
    }
  }

  .el-table__row:nth-child(even) {
    background-color: #fafbfc;
  }

  .el-table__row:hover {
    background-color: #eff6ff;
  }

  .el-table__body tr:hover > td {
    background-color: #eff6ff;
  }
}

.info-filter-container {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid #f5f7fa;

  .filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f2f5;

    .filter-title {
      font-size: 15px;
      font-weight: 600;
      color: #1a1a1a;
      letter-spacing: 0.3px;
    }
  }

  .filter-body {
    .filter-row {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 8px 0;
    }

    .filter-item {
      display: flex;
      align-items: center;
      gap: 10px;

      &.filter-item-wide {
        flex: 1;
        min-width: 200px;
      }
    }

    .filter-label {
      font-size: 13px;
      color: #666666;
      white-space: nowrap;
      width: 56px;
      flex-shrink: 0;
      font-weight: 500;
      letter-spacing: 0.2px;
      padding-top: 6px;
    }

    .filter-input {
      width: 150px;

      &:deep(.el-input__wrapper) {
        border-radius: 8px;
        border-color: #e8ecf0;
        box-shadow: none;
        transition: all 0.25s ease;

        &:hover {
          border-color: #cdd5de;
        }

        &.is-focus {
          border-color: #4286f5;
          box-shadow: 0 0 0 3px rgba(66, 134, 245, 0.1);
        }
      }

      &:deep(.el-input__inner) {
        font-size: 13px;
        color: #333;
        height: 32px;
        line-height: 32px;
      }

      &:deep(.el-select__wrapper) {
        border-radius: 8px;
        border-color: #e8ecf0;
        box-shadow: none;
        transition: all 0.25s ease;

        &:hover {
          border-color: #cdd5de;
        }

        &.is-focus {
          border-color: #4286f5;
          box-shadow: 0 0 0 3px rgba(66, 134, 245, 0.1);
        }
      }

      &:deep(.el-select__placeholder) {
        font-size: 13px;
        color: #999;
      }

      &:deep(.el-select__input) {
        font-size: 13px;
        color: #333;
      }

      &:deep(.el-input__clear) {
        color: #c0c4cc;
        font-size: 14px;

        &:hover {
          color: #666;
        }
      }
    }

    .filter-actions {
      display: flex;
      gap: 10px;

      :deep(.el-button) {
        font-size: 13px;
        padding: 6px 16px;
        border-radius: 8px;
        font-weight: 500;
        transition: all 0.25s ease;

        &.el-button--primary {
          background: linear-gradient(135deg, #4286f5 0%, #357ae8 100%);
          border: none;
          color: #ffffff;
          box-shadow: 0 2px 8px rgba(66, 134, 245, 0.3);

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(66, 134, 245, 0.4);
          }
        }

        &:not(.el-button--primary) {
          background: #f5f7fa;
          border-color: #e8ecf0;
          color: #666;

          &:hover {
            background: #edf1f5;
            border-color: #dce3ea;
          }
        }
      }
    }
  }
}

// 加载和错误状态样式
.loading-container,
.error-container {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.footer {
  margin-top: 60px;

  .footer-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, #e0e0e0, transparent);
    margin-bottom: 40px;
  }

  .footer-content {
    width: @pc-container-width;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    padding-bottom: 40px;

    .footer-left {
      flex: 1;

      .footer-logo {
        margin-bottom: 20px;
      }

      .social-icons {
        display: flex;
        gap: 20px;
        margin-bottom: 20px;

        .social-icon {
          width: 40px;
          height: 40px;
          cursor: pointer;
          opacity: 0.7;
          transition: opacity 0.3s ease;

          &:hover {
            opacity: 1;
          }

          img {
            width: 100%;
            height: 100%;
          }
        }
      }

      .copyright {
        font-size: 14px;
        color: #999;
        margin-bottom: 8px;
      }

      .icp {
        font-size: 13px;
        color: #bbb;

        a {
          color: #bbb;
          text-decoration: none;

          &:hover {
            color: #4689f2;
          }
        }
      }
    }

    .footer-right {
      display: flex;
      gap: 60px;

      .footer-column {
        .column-title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin-bottom: 15px;
        }

        .column-link {
          display: block;
          font-size: 14px;
          color: #666;
          margin-bottom: 10px;
          text-decoration: none;
          transition: color 0.3s ease;

          &:hover {
            color: #4689f2;
          }
        }
      }
    }
  }
}

.filter-options {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
}

.brand-filter-options {
  flex-wrap: nowrap;
  overflow: hidden;
}

.brand-options-scroll {
  display: flex;
  flex: 1;
  min-width: 0;
  gap: 8px 10px;
  overflow: hidden;
}

.brand-options-scroll .filter-chip,
.brand-filter-options .filter-more {
  flex: 0 0 auto;
}

.brand-filter-options .filter-more {
  padding-left: 12px;
  background: #fff;
}

.filter-chip,
.filter-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  font-size: 13px;
  color: #606266;
  padding: 0 11px;
  border-radius: 4px;
  cursor: pointer;
  line-height: 1;
  white-space: nowrap;
}

.filter-chip:hover,
.filter-more {
  color: #1989fa;
}

.filter-chip.active {
  color: #1989fa;
  background: #e8f4ff;
}

.brand-search-results {
  max-height: 120px;
  margin-top: 0;
  overflow-y: auto;
  border: 1px solid #dcdfe6;
  border-top: 0;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  position: relative;
  z-index: 1;
}

.brand-search-item,
.brand-search-status {
  padding: 10px 12px;
  font-size: 14px;
  color: #606266;
}

.brand-search-item {
  cursor: pointer;
}

.brand-search-item:hover,
.brand-search-item.active {
  color: #1989fa;
  background: #ecf5ff;
}

.brand-search-status {
  color: #909399;
  text-align: center;
}

.filter-input-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-input-wrapper .filter-input {
  flex: 0 0 40%;
  max-width: 40%;
}
</style>
